import { useRef, useState } from 'react';
import { faSortDown } from '@fortawesome/pro-solid-svg-icons/faSortDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Anchor, Button, Checkbox, Group, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useIntl } from 'react-intl';

import { notificationUpdatesPickerMessages } from './notification-updates-picker-messages';
import { NOTIFICATION_TYPE_OPTIONS } from './notifications-section-messages';
import { NotificationTypePills } from './NotificationTypePills';
import styles from './NotificationUpdatesPicker.module.css';

import type { FreightNotificationType } from '../../../../types/freight-responses';

export type NotificationUpdatesPickerProps = {
  value: FreightNotificationType[];
  onChange: (next: FreightNotificationType[]) => void;
  error?: string;
  'data-testid'?: string;
};

export const NotificationUpdatesPicker = ({
  value,
  onChange,
  error,
  'data-testid': dataTestId = 'notification-updates-picker',
}: NotificationUpdatesPickerProps) => {
  const intl = useIntl();
  const chevronRef = useRef<HTMLButtonElement>(null);
  const [isOpened, { open, close }] = useDisclosure(false);
  const [draftValues, setDraftValues] = useState<FreightNotificationType[]>(value);

  // Reopening always restarts from the committed value so an abandoned draft is discarded.
  const handleOpenChange = (nextOpened: boolean) => {
    if (nextOpened) {
      setDraftValues(value);
      open();
    } else {
      close();
    }
  };

  const handleApply = () => {
    onChange(draftValues);
    close();
    chevronRef.current?.focus();
  };

  const handleRemove = (type: FreightNotificationType) => {
    onChange(value.filter((selected) => selected !== type));
    setDraftValues((previous) => previous.filter((selected) => selected !== type));
  };

  const isEverythingSelected = draftValues.length === NOTIFICATION_TYPE_OPTIONS.length;

  const handleToggleAll = () => setDraftValues(isEverythingSelected ? [] : NOTIFICATION_TYPE_OPTIONS.map((option) => option.value));

  return (
    <Stack gap={4}>
      <Popover onChange={handleOpenChange} opened={isOpened} position='bottom-start' trapFocus width='target'>
        <Popover.Target>
          <div
            className={styles.trigger}
            data-error={error ? true : undefined}
            data-expanded={isOpened ? true : undefined}
            data-placeholder={value.length === 0 ? true : undefined}
            data-testid={dataTestId}
            onClick={() => handleOpenChange(!isOpened)}
          >
            {value.length > 0 ? (
              <NotificationTypePills onRemove={handleRemove} types={value} />
            ) : (
              <span>{intl.formatMessage(notificationUpdatesPickerMessages.placeholder)}</span>
            )}
            <UnstyledButton
              ref={chevronRef}
              aria-expanded={isOpened}
              aria-haspopup='dialog'
              aria-label={intl.formatMessage(notificationUpdatesPickerMessages.placeholder)}
              className={styles.chevronButton}
              type='button'
            >
              <FontAwesomeIcon className={styles.chevron} icon={faSortDown} />
            </UnstyledButton>
          </div>
        </Popover.Target>
        <Popover.Dropdown className={styles.dropdown}>
          <Stack gap='xs'>
            <Group justify='flex-end'>
              <Anchor component='button' data-testid={`${dataTestId}-toggle-all`} onClick={handleToggleAll} size='sm' type='button'>
                {intl.formatMessage(
                  isEverythingSelected ? notificationUpdatesPickerMessages.clearAll : notificationUpdatesPickerMessages.selectAll,
                )}
              </Anchor>
            </Group>
            <Checkbox.Group onChange={(next) => setDraftValues(next as FreightNotificationType[])} value={draftValues}>
              <Stack gap='xs'>
                {NOTIFICATION_TYPE_OPTIONS.map((option, index) => (
                  <Checkbox
                    data-autofocus={index === 0 ? true : undefined}
                    key={option.value}
                    label={intl.formatMessage(option.label)}
                    value={option.value}
                  />
                ))}
              </Stack>
            </Checkbox.Group>
            <Group justify='flex-end'>
              <Button data-testid={`${dataTestId}-apply`} onClick={handleApply} size='compact-sm' variant='primary'>
                {intl.formatMessage(notificationUpdatesPickerMessages.apply)}
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
      {error && (
        <Text c='var(--adl-border-error)' size='xs'>
          {error}
        </Text>
      )}
    </Stack>
  );
};
